import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

import { useSettings } from '../../context/SettingsContext';
import { API_URL } from '../Constants';

const Signup = () => {
  const settings = useSettings();
  const navigate = useNavigate();
  
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    how_work: '',
    will_pay: 0
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user starts typing
  };

  const handleWillPayChange = (value) => {
    setFormData(prev => ({
      ...prev,
      will_pay: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Create FormData object
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      const response = await axios.post(`${API_URL}validator_add`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setSubmitted(true);
      } else {
        setError(response.data.error || 'An error occurred. Please try again.');
      }
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <nav className="w-full fixed z-50">
          <div className="w-full mx-auto bg-primary lg:py-3 py-5">
            <h1 className='text-white text-center my-0'>Valparx Validation</h1>
          </div>
        </nav>

        <div className='flex items-center justify-center min-h-screen pt-20'>
          <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
            <div className='mb-6'>
              <svg 
                className='mx-auto h-16 w-16 text-green-500' 
                fill='none' 
                stroke='currentColor' 
                viewBox='0 0 24 24'
              >
                <path 
                  strokeLinecap='round' 
                  strokeLinejoin='round' 
                  strokeWidth={2} 
                  d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' 
                />
              </svg>
            </div>
            <h2 className='text-2xl font-bold text-gray-900 mb-4'>
              Thank You for Your Submission!
            </h2>
            <p className='text-gray-600 mb-8'>
              {successMessage}
            </p>
            <button
              onClick={() => navigate('/')}
              className='w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200'
            >
              Return to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50'>
      <nav className="w-full fixed z-50">
        <div className="w-full mx-auto bg-primary lg:py-3 py-5">
          <h1 className='text-white text-center my-0'>Valparx Validation</h1>
        </div>
      </nav>

      <div className='flex items-center justify-center min-h-screen py-20'>
        <div className='max-w-2xl mx-auto'>
          <div className='bg-white rounded-lg shadow-lg p-8'>
            <div className='mb-8'>
              <h1 className='text-3xl font-bold text-center text-gray-900 mb-4'>
                Apply for Validator Account
              </h1>
              <p className='text-gray-600'>
                Validator accounts must be approved by ValParx before they can validate parking. 
                Please fill out the form below and our team will review your application.
              </p>
            </div>

            {error && (
              <div className='mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg'>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className='space-y-6'>
              {/* Name */}
              <div>
                <label htmlFor='name' className='block text-md font-medium text-gray-700 mb-2'>
                  Name <span className='text-red-500'>*</span>
                </label>
                <input
                  type='text'
                  id='name'
                  name='name'
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                />
              </div>

              {/* Company */}
              <div>
                <label htmlFor='company' className='block text-md font-medium text-gray-700 mb-2'>
                  Company <span className='text-red-500'>*</span>
                </label>
                <input
                  type='text'
                  id='company'
                  name='company'
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor='phone' className='block text-md font-medium text-gray-700 mb-2'>
                  Phone <span className='text-red-500'>*</span>
                </label>
                <input
                  type='tel'
                  id='phone'
                  name='phone'
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                />
                <p className='mt-1 text-md text-gray-500'>
                  This will be required for login
                </p>
              </div>

              {/* Email */}
              <div>
                <label htmlFor='email' className='block text-md font-medium text-gray-700 mb-2'>
                  Email <span className='text-red-500'>*</span>
                </label>
                <input
                  type='email'
                  id='email'
                  name='email'
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                />
                <p className='mt-1 text-md text-gray-500'>
                  This will be required for login
                </p>
              </div>

              {/* Address */}
              <div>
                <label htmlFor='address' className='block text-md font-medium text-gray-700 mb-2'>
                  Address <span className='text-red-500'>*</span>
                </label>
                <input
                  type='text'
                  id='address'
                  name='address'
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                />
              </div>

              {/* Address 2 */}
              <div>
                <label htmlFor='address2' className='block text-md font-medium text-gray-700 mb-2'>
                  Address Line 2
                </label>
                <input
                  type='text'
                  id='address2'
                  name='address2'
                  value={formData.address2}
                  onChange={handleInputChange}
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                />
              </div>

              {/* City, State, Zip Row */}
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                {/* City */}
                <div className='md:col-span-1'>
                  <label htmlFor='city' className='block text-md font-medium text-gray-700 mb-2'>
                    City <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    id='city'
                    name='city'
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                  />
                </div>

                {/* State */}
                <div>
                  <label htmlFor='state' className='block text-md font-medium text-gray-700 mb-2'>
                    State <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    id='state'
                    name='state'
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    maxLength={2}
                    className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition uppercase'
                    placeholder='IL'
                  />
                </div>

                {/* Zip */}
                <div>
                  <label htmlFor='zip' className='block text-md font-medium text-gray-700 mb-2'>
                    Zip <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    id='zip'
                    name='zip'
                    value={formData.zip}
                    onChange={handleInputChange}
                    required
                    maxLength={5}
                    pattern='[0-9]{5}'
                    className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                    placeholder='60601'
                  />
                </div>
              </div>

              {/* How Work */}
              <div>
                <label htmlFor='how_work' className='block text-md font-medium text-gray-700 mb-2'>
                  How the Validation will work: <span className='text-gray-500'>[example, "Free Parking" or "2 Hours Free"]</span> <span className='text-red-500'>*</span>
                </label>
                <input
                  type='text'
                  id='how_work'
                  name='how_work'
                  value={formData.how_work}
                  onChange={handleInputChange}
                  required
                  className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                  placeholder='e.g., Free Parking'
                />
              </div>

              {/* Will Pay */}
              <div>
                <label className='block text-md font-medium text-gray-700 mb-3'>
                  Will the validator pay you anything for this? <span className='text-red-500'>*</span>
                </label>
                <div className='flex gap-6'>
                  <label className='flex items-center cursor-pointer'>
                    <input
                      type='radio'
                      name='will_pay'
                      value={1}
                      checked={formData.will_pay === 1 || formData.will_pay === '1'}
                      onChange={(e) => handleWillPayChange(1)}
                      className='w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500'
                    />
                    <span className='ml-2 text-gray-700'>Yes</span>
                  </label>
                  <label className='flex items-center cursor-pointer'>
                    <input
                      type='radio'
                      name='will_pay'
                      value={0}
                      checked={formData.will_pay === 0 || formData.will_pay === '0'}
                      onChange={(e) => handleWillPayChange(0)}
                      className='w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500'
                    />
                    <span className='ml-2 text-gray-700'>No</span>
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <div className='pt-4'>
                <button
                  type='submit'
                  disabled={isSubmitting}
                  className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>

            <div className='mt-6 text-center'>
              <Link to='/' className='text-blue-600 hover:text-blue-700 text-md'>
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
